import {
	DndContext,
	type DragEndEvent,
	DragOverlay,
	type DragStartEvent,
	MouseSensor,
	TouchSensor,
	useDraggable,
	useDroppable,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import {
	SortableContext,
	arrayMove,
	useSortable,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { getPairCredits, getPointsPerPair } from "@memorang/helpers/ordering";
import type { Choice } from "@memorang/types";
import Icon from "@react-native-vector-icons/material-design-icons/static";
import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { IconButton, Text } from "react-native-paper";
import { useAppTheme } from "../../../../../useAppTheme";
import { useDevice } from "../../../../../useDevice";
import Box from "../../../../Box";
import { ItemInstruction } from "../pte/ItemInstruction";
import { PairSeparator } from "./PairSeparator";
import { ReviewScoreChip, ReviewScoreRow } from "./ReviewScoreChip";
import {
	type ReorderParagraphProps as Props,
	useCardColors,
} from "./reorder-paragraph-utils";

const SOURCE_ZONE_ID = "source-zone";
const ANSWER_ZONE_ID = "answer-zone";
const SOURCE_ITEM_PREFIX = "src:";

type DragData = {
	id: string;
	from: "source" | "answer";
};

type Selection = { id: string; zone: "source" | "answer" };

type ArrowAction = {
	icon: string;
	onPress: () => void;
	disabled: boolean;
};

const ParagraphCardContent = ({
	text,
	dragging,
	selected,
	disabled,
}: {
	text: string;
	dragging?: boolean;
	selected?: boolean;
	disabled?: boolean;
}) => {
	const theme = useAppTheme();
	const { borderColor, backgroundColor } = useCardColors("neutral", selected);

	return (
		<Box
			flexDirection="row"
			alignItems="center"
			justifyContent="space-between"
			gap={16}
			paddingHorizontal={16}
			paddingVertical={8}
			minHeight={56}
			borderRadius={theme.roundness * 2}
			borderWidth={1}
			borderColor={borderColor}
			backgroundColor={backgroundColor}
			style={{
				boxShadow: dragging
					? "0 4px 16px rgba(0,0,0,0.15)"
					: selected
						? `0 0 0 1px ${theme.colors.primary}`
						: "none",
				boxSizing: "border-box",
			}}
		>
			<Text variant="bodyLarge" style={{ flex: 1 }}>
				{text}
			</Text>
			<Icon
				name="drag-vertical"
				size={24}
				color={
					disabled
						? theme.colors.onSurfaceDisabled
						: theme.colors.onSurfaceVariant
				}
			/>
		</Box>
	);
};

const SourceCard = ({
	paragraph,
	disabled,
	selected,
	onSelect,
}: {
	paragraph: Choice;
	disabled?: boolean;
	selected?: boolean;
	onSelect?: (id: string) => void;
}) => {
	const { attributes, listeners, setNodeRef, transform, isDragging } =
		useDraggable({
			id: `${SOURCE_ITEM_PREFIX}${paragraph.id}`,
			data: { id: paragraph.id, from: "source" } satisfies DragData,
			disabled,
		});

	const select = () => {
		if (!disabled) {
			onSelect?.(paragraph.id);
		}
	};

	return (
		<div
			ref={setNodeRef}
			onClick={select}
			onFocus={select}
			style={{
				transform: isDragging ? undefined : CSS.Translate.toString(transform),
				opacity: isDragging ? 0.4 : 1,
				cursor: disabled ? "default" : "grab",
				outline: "none",
				touchAction: "manipulation",
			}}
			{...listeners}
			{...attributes}
		>
			<ParagraphCardContent
				text={paragraph.text ?? ""}
				selected={selected}
				disabled={disabled}
			/>
		</div>
	);
};

const SortableAnswerCard = ({
	paragraph,
	disabled,
	selected,
	onSelect,
}: {
	paragraph: Choice;
	disabled?: boolean;
	selected?: boolean;
	onSelect?: (id: string) => void;
}) => {
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({
		id: paragraph.id,
		data: { id: paragraph.id, from: "answer" } satisfies DragData,
		disabled,
	});

	const select = () => {
		if (!disabled) {
			onSelect?.(paragraph.id);
		}
	};

	return (
		<div
			ref={setNodeRef}
			onClick={select}
			onFocus={select}
			style={{
				transform: isDragging ? undefined : CSS.Translate.toString(transform),
				transition,
				opacity: isDragging ? 0.4 : 1,
				cursor: disabled ? "default" : "grab",
				outline: "none",
				touchAction: "manipulation",
			}}
			{...listeners}
			{...attributes}
		>
			<ParagraphCardContent
				text={paragraph.text ?? ""}
				selected={selected}
				disabled={disabled}
			/>
		</div>
	);
};

const ZoneHeader = ({
	title,
	badgeLabel,
	scoreChip,
}: {
	title: string;
	badgeLabel: string;
	scoreChip?: React.ReactNode;
}) => {
	const theme = useAppTheme();
	return (
		<Box
			flexDirection="row"
			justifyContent="space-between"
			alignItems="center"
			marginBottom={16}
		>
			<Text variant="titleSmall">{title}</Text>
			<Box flexDirection="row" alignItems="center" gap={8}>
				{scoreChip}
				<Box
					paddingHorizontal={8}
					paddingVertical={6}
					borderRadius={theme.roundness * 2}
					backgroundColor={theme.colors.primaryContainer}
				>
					<Text
						variant="labelMedium"
						style={{ color: theme.colors.onPrimaryContainer }}
					>
						{badgeLabel}
					</Text>
				</Box>
			</Box>
		</Box>
	);
};

const SourceZone = ({
	availableParagraphs,
	activeDrag,
	disabled,
	selectedId,
	onSelect,
}: {
	availableParagraphs: Choice[];
	activeDrag: DragData | null;
	disabled?: boolean;
	selectedId?: string;
	onSelect?: (id: string) => void;
}) => {
	const { setNodeRef, isOver } = useDroppable({
		id: SOURCE_ZONE_ID,
		disabled,
	});
	const theme = useAppTheme();
	const { t } = useTranslation();
	const highlight = isOver || activeDrag?.from === "answer";
	return (
		<div
			ref={setNodeRef}
			style={{
				flex: 1,
				padding: 16,
				borderRadius: theme.roundness * 2,
				border: `${highlight ? "1px dashed" : "1px solid"} ${
					highlight ? theme.colors.surfaceVariant : theme.colors.outlineVariant
				}`,
				backgroundColor: highlight
					? theme.colors.surfaceVariant
					: theme.colors.surface,
				minHeight: 560,
				boxSizing: "border-box",
			}}
		>
			<ZoneHeader
				title={t("testDriver.itemTypes.reorderParagraph.sourceTitle")}
				badgeLabel={t("testDriver.itemTypes.reorderParagraph.remainingBadge", {
					count: availableParagraphs.length,
				})}
			/>
			<Box flexDirection="column" gap={16}>
				{availableParagraphs.map((p) => (
					<SourceCard
						key={p.id}
						paragraph={p}
						disabled={disabled}
						selected={selectedId === p.id}
						onSelect={onSelect}
					/>
				))}
			</Box>
		</div>
	);
};

const AnswerZone = ({
	placedParagraphs,
	activeDrag,
	disabled,
	selectedId,
	onSelect,
	pairCredits,
	pointsPerPair = 1,
	scoreChip,
}: {
	placedParagraphs: Choice[];
	activeDrag: DragData | null;
	disabled?: boolean;
	selectedId?: string;
	onSelect?: (id: string) => void;
	pairCredits?: (boolean | undefined)[];
	pointsPerPair?: number;
	scoreChip?: React.ReactNode;
}) => {
	const { setNodeRef, isOver } = useDroppable({
		id: ANSWER_ZONE_ID,
		disabled,
	});
	const theme = useAppTheme();
	const { t } = useTranslation();
	const isEmpty = placedParagraphs.length === 0;
	const highlight = isOver || activeDrag?.from === "source";

	return (
		<div
			ref={setNodeRef}
			style={{
				flex: 1,
				padding: 16,
				borderRadius: theme.roundness * 2,
				border: `${highlight ? "1px dashed" : "1px solid"} ${
					highlight ? theme.colors.surfaceVariant : theme.colors.outlineVariant
				}`,
				backgroundColor: highlight
					? theme.colors.surfaceVariant
					: theme.colors.surface,
				minHeight: 560,
				display: "flex",
				flexDirection: "column",
				boxSizing: "border-box",
			}}
		>
			<ZoneHeader
				title={t("testDriver.itemTypes.reorderParagraph.answerTitle")}
				badgeLabel={t("testDriver.itemTypes.reorderParagraph.placedBadge", {
					count: placedParagraphs.length,
				})}
				scoreChip={scoreChip}
			/>
			{isEmpty ? (
				<Box
					flex={1}
					flexDirection="column"
					alignItems="center"
					justifyContent="center"
					gap={8}
				>
					<Icon
						name="selection-drag"
						size={24}
						color={theme.colors.onSurfaceVariant}
					/>
					<Text variant="labelMedium">
						{t("testDriver.itemTypes.reorderParagraph.emptyHint")}
					</Text>
				</Box>
			) : (
				<SortableContext
					items={placedParagraphs.map((p) => p.id)}
					strategy={verticalListSortingStrategy}
				>
					<Box flexDirection="column" gap={pairCredits ? 0 : 16}>
						{placedParagraphs.map((p, index) => (
							<Box key={p.id}>
								{pairCredits && index > 0 ? (
									<PairSeparator
										credited={pairCredits[index - 1]}
										pointsPerPair={pointsPerPair}
									/>
								) : null}
								<SortableAnswerCard
									paragraph={p}
									disabled={disabled}
									selected={selectedId === p.id}
									onSelect={onSelect}
								/>
							</Box>
						))}
					</Box>
				</SortableContext>
			)}
		</div>
	);
};

const ArrowButtonColumn = ({
	direction,
	actions,
}: {
	direction: "horizontal" | "vertical";
	actions: ArrowAction[];
}) => {
	return (
		<Box
			flexDirection={direction === "horizontal" ? "row" : "column"}
			gap={8}
			alignItems="center"
			alignSelf="center"
		>
			{actions.map((action) => (
				<IconButton
					key={action.icon}
					icon={action.icon}
					size={20}
					mode="outlined"
					disabled={action.disabled}
					style={{ width: 32, height: 32, margin: 0 }}
					onPress={action.onPress}
				/>
			))}
		</Box>
	);
};

const isSourceTarget = (overId: string) =>
	overId === SOURCE_ZONE_ID || overId.startsWith(SOURCE_ITEM_PREFIX);

const handleSourceDrop = (
	placed: string[],
	activeId: string,
	overId: string,
): string[] | null => {
	if (isSourceTarget(overId)) {
		return null;
	}
	const next = [...placed];
	if (overId === ANSWER_ZONE_ID) {
		next.push(activeId);
		return next;
	}
	const targetIndex = next.indexOf(overId);
	if (targetIndex >= 0) {
		next.splice(targetIndex, 0, activeId);
	} else {
		next.push(activeId);
	}
	return next;
};

const handleAnswerDrop = (
	placed: string[],
	activeId: string,
	overId: string,
): string[] | null => {
	if (isSourceTarget(overId)) {
		return placed.filter((id) => id !== activeId);
	}
	if (overId === ANSWER_ZONE_ID) {
		return null;
	}
	const oldIndex = placed.indexOf(activeId);
	const newIndex = placed.indexOf(overId);
	if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) {
		return null;
	}
	return arrayMove(placed, oldIndex, newIndex);
};

const useSelectionControls = ({
	selection,
	setSelection,
	availableParagraphs,
	placedParagraphs,
	resolvedPlacedIds,
	handleChange,
	interactionDisabled,
}: {
	selection: Selection | null;
	setSelection: (s: Selection | null) => void;
	availableParagraphs: Choice[];
	placedParagraphs: Choice[];
	resolvedPlacedIds: string[];
	handleChange: (next: string[]) => void;
	interactionDisabled: boolean;
}) => {
	useEffect(() => {
		if (interactionDisabled) {
			setSelection(null);
		}
	}, [interactionDisabled, setSelection]);

	useEffect(() => {
		if (!selection) {
			return;
		}
		const inZone =
			selection.zone === "source"
				? availableParagraphs.some((p) => p.id === selection.id)
				: placedParagraphs.some((p) => p.id === selection.id);
		if (!inZone) {
			setSelection(null);
		}
	}, [selection, availableParagraphs, placedParagraphs, setSelection]);

	const handleSelectSource = useCallback(
		(id: string) => setSelection({ id, zone: "source" }),
		[setSelection],
	);

	const handleSelectAnswer = useCallback(
		(id: string) => setSelection({ id, zone: "answer" }),
		[setSelection],
	);

	const handleMoveToAnswer = useCallback(() => {
		if (selection?.zone !== "source") {
			return;
		}
		handleChange([...resolvedPlacedIds, selection.id]);
		setSelection(null);
	}, [selection, resolvedPlacedIds, handleChange, setSelection]);

	const handleMoveToSource = useCallback(() => {
		if (selection?.zone !== "answer") {
			return;
		}
		handleChange(resolvedPlacedIds.filter((id) => id !== selection.id));
		setSelection(null);
	}, [selection, resolvedPlacedIds, handleChange, setSelection]);

	const handleMoveUp = useCallback(() => {
		if (selection?.zone !== "answer") {
			return;
		}
		const idx = resolvedPlacedIds.indexOf(selection.id);
		if (idx <= 0) {
			return;
		}
		handleChange(arrayMove(resolvedPlacedIds, idx, idx - 1));
	}, [selection, resolvedPlacedIds, handleChange]);

	const handleMoveDown = useCallback(() => {
		if (selection?.zone !== "answer") {
			return;
		}
		const idx = resolvedPlacedIds.indexOf(selection.id);
		if (idx < 0 || idx >= resolvedPlacedIds.length - 1) {
			return;
		}
		handleChange(arrayMove(resolvedPlacedIds, idx, idx + 1));
	}, [selection, resolvedPlacedIds, handleChange]);

	const selectedAnswerIndex =
		selection?.zone === "answer" ? resolvedPlacedIds.indexOf(selection.id) : -1;

	const horizontalActions: ArrowAction[] = [
		{
			icon: "chevron-left",
			onPress: handleMoveToSource,
			disabled: interactionDisabled || selection?.zone !== "answer",
		},
		{
			icon: "chevron-right",
			onPress: handleMoveToAnswer,
			disabled: interactionDisabled || selection?.zone !== "source",
		},
	];

	const verticalActions: ArrowAction[] = [
		{
			icon: "chevron-up",
			onPress: handleMoveUp,
			disabled:
				interactionDisabled ||
				selection?.zone !== "answer" ||
				selectedAnswerIndex <= 0,
		},
		{
			icon: "chevron-down",
			onPress: handleMoveDown,
			disabled:
				interactionDisabled ||
				selection?.zone !== "answer" ||
				selectedAnswerIndex < 0 ||
				selectedAnswerIndex >= resolvedPlacedIds.length - 1,
		},
	];

	return {
		handleSelectSource,
		handleSelectAnswer,
		horizontalActions,
		verticalActions,
	};
};

const ReorderParagraph = ({
	instruction,
	paragraphs,
	values,
	onChangeValues,
	disabled = false,
	isAnswered,
	inReviewMode,
	correctOrder,
	earnedPoints,
	maxPoints,
	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: na
}: Props) => {
	const { isMobile } = useDevice();
	const showReviewState = Boolean(isAnswered || inReviewMode);
	const interactionDisabled = disabled || showReviewState;

	const [localPlacedIds, setLocalPlacedIds] = useState<string[]>([]);
	const [activeDrag, setActiveDrag] = useState<DragData | null>(null);
	const [selection, setSelection] = useState<Selection | null>(null);

	// values may be non-array at runtime (malformed answer/store) — APPS-GTZ
	const safeValues = Array.isArray(values) ? values : undefined;
	const resolvedPlacedIds = safeValues ?? localPlacedIds;

	const sensors = useSensors(
		useSensor(MouseSensor, {
			activationConstraint: { distance: 8 },
		}),
		useSensor(TouchSensor, {
			activationConstraint: { delay: 250, tolerance: 5 },
		}),
	);

	const paragraphsById = useMemo(
		() => new Map(paragraphs.map((p) => [p.id, p])),
		[paragraphs],
	);

	const handleChange = useCallback(
		(nextPlaced: string[]) => {
			onChangeValues?.(nextPlaced);
			if (!safeValues) {
				setLocalPlacedIds(nextPlaced);
			}
		},
		[onChangeValues, safeValues],
	);

	useEffect(() => {
		if (safeValues) {
			return;
		}
		setLocalPlacedIds((prev) => {
			const valid = prev.filter((id) => paragraphsById.has(id));
			return valid.length === prev.length ? prev : valid;
		});
	}, [paragraphsById, safeValues]);

	const placedParagraphs = useMemo(
		() =>
			resolvedPlacedIds
				.map((id) => paragraphsById.get(id))
				.filter((p): p is Choice => Boolean(p)),
		[resolvedPlacedIds, paragraphsById],
	);

	const availableParagraphs = useMemo(() => {
		const placedSet = new Set(resolvedPlacedIds);
		return paragraphs.filter((p) => !placedSet.has(p.id));
	}, [paragraphs, resolvedPlacedIds]);

	const singleListOrderFromValues = useMemo(() => {
		if (!safeValues || safeValues.length === 0) {
			return null;
		}
		const placedSet = new Set(safeValues);
		const placed = safeValues.filter((id) => paragraphsById.has(id));
		const remaining = paragraphs
			.map((p) => p.id)
			.filter((id) => !placedSet.has(id));
		return [...placed, ...remaining];
	}, [safeValues, paragraphs, paragraphsById]);

	const [localSingleListOrder, setLocalSingleListOrder] = useState<string[]>(
		() => paragraphs.map((p) => p.id),
	);

	useEffect(() => {
		if (singleListOrderFromValues) {
			return;
		}
		setLocalSingleListOrder((prev) => {
			const prevSet = new Set(prev);
			const sameSet =
				prevSet.size === paragraphs.length &&
				paragraphs.every((p) => prevSet.has(p.id));
			return sameSet ? prev : paragraphs.map((p) => p.id);
		});
	}, [paragraphs, singleListOrderFromValues]);

	const resolvedSingleListIds =
		singleListOrderFromValues ?? localSingleListOrder;
	const singleListParagraphs = resolvedSingleListIds
		.map((id) => paragraphsById.get(id))
		.filter((p): p is Choice => Boolean(p));

	const handleDragStart = useCallback(({ active }: DragStartEvent) => {
		setActiveDrag((active.data.current as DragData | undefined) ?? null);
	}, []);

	const handleDragCancel = useCallback(() => {
		setActiveDrag(null);
	}, []);

	const handleDesktopDragEnd = useCallback(
		({ active, over }: DragEndEvent) => {
			setActiveDrag(null);
			if (!over) {
				return;
			}
			const data = active.data.current as DragData | undefined;
			if (!data) {
				return;
			}
			const overId = String(over.id);
			const next =
				data.from === "source"
					? handleSourceDrop(resolvedPlacedIds, data.id, overId)
					: handleAnswerDrop(resolvedPlacedIds, data.id, overId);
			if (next) {
				handleChange(next);
			}
		},
		[handleChange, resolvedPlacedIds],
	);

	const handleSingleListDragEnd = useCallback(
		({ active, over }: DragEndEvent) => {
			setActiveDrag(null);
			if (!over || active.id === over.id) {
				return;
			}
			const oldIndex = resolvedSingleListIds.indexOf(String(active.id));
			const newIndex = resolvedSingleListIds.indexOf(String(over.id));
			if (oldIndex < 0 || newIndex < 0) {
				return;
			}
			const nextOrder = arrayMove(resolvedSingleListIds, oldIndex, newIndex);
			if (!safeValues) {
				setLocalSingleListOrder(nextOrder);
			}
			onChangeValues?.(nextOrder);
		},
		[resolvedSingleListIds, onChangeValues, safeValues],
	);

	const activeParagraph = activeDrag
		? paragraphsById.get(activeDrag.id)
		: undefined;

	const dragOverlay = createPortal(
		<DragOverlay>
			{activeParagraph ? (
				<ParagraphCardContent text={activeParagraph.text ?? ""} dragging />
			) : null}
		</DragOverlay>,
		document.body,
	);

	const {
		handleSelectSource,
		handleSelectAnswer,
		horizontalActions,
		verticalActions,
	} = useSelectionControls({
		selection,
		setSelection,
		availableParagraphs,
		placedParagraphs,
		resolvedPlacedIds,
		handleChange,
		interactionDisabled,
	});

	const pointsPerPair = getPointsPerPair(correctOrder, maxPoints);
	const scoreChip = showReviewState ? (
		<ReviewScoreChip earnedPoints={earnedPoints} maxPoints={maxPoints} />
	) : null;

	const reviewCredits = showReviewState
		? {
				answer: getPairCredits(
					resolvedPlacedIds,
					resolvedPlacedIds.length,
					correctOrder,
				),
				singleList: getPairCredits(
					resolvedSingleListIds,
					safeValues?.length ?? 0,
					correctOrder,
				),
			}
		: undefined;
	const answerPairCredits = reviewCredits?.answer;
	const singleListPairCredits = reviewCredits?.singleList;

	if (isMobile) {
		return (
			<Box gap={16}>
				<ItemInstruction instruction={instruction} />
				{showReviewState ? (
					<ReviewScoreRow earnedPoints={earnedPoints} maxPoints={maxPoints} />
				) : null}
				<DndContext
					sensors={sensors}
					onDragStart={handleDragStart}
					onDragEnd={handleSingleListDragEnd}
					onDragCancel={handleDragCancel}
				>
					<SortableContext
						items={resolvedSingleListIds}
						strategy={verticalListSortingStrategy}
					>
						<Box flexDirection="column" gap={singleListPairCredits ? 0 : 16}>
							{singleListParagraphs.map((p, index) => (
								<Box key={p.id}>
									{singleListPairCredits && index > 0 ? (
										<PairSeparator
											credited={singleListPairCredits[index - 1]}
											pointsPerPair={pointsPerPair}
										/>
									) : null}
									<SortableAnswerCard
										paragraph={p}
										disabled={interactionDisabled}
									/>
								</Box>
							))}
						</Box>
					</SortableContext>
					{dragOverlay}
				</DndContext>
			</Box>
		);
	}

	return (
		<Box gap={16}>
			<ItemInstruction instruction={instruction} />
			<DndContext
				sensors={sensors}
				autoScroll={{ threshold: { x: 0, y: 0.2 } }}
				onDragStart={handleDragStart}
				onDragEnd={handleDesktopDragEnd}
				onDragCancel={handleDragCancel}
			>
				<DesktopLayout
					availableParagraphs={availableParagraphs}
					placedParagraphs={placedParagraphs}
					activeDrag={activeDrag}
					disabled={interactionDisabled}
					selection={selection}
					onSelectSource={handleSelectSource}
					onSelectAnswer={handleSelectAnswer}
					horizontalActions={horizontalActions}
					verticalActions={verticalActions}
					pairCredits={answerPairCredits}
					pointsPerPair={pointsPerPair}
					scoreChip={scoreChip}
				/>
				{dragOverlay}
			</DndContext>
		</Box>
	);
};

const DesktopLayout = ({
	availableParagraphs,
	placedParagraphs,
	activeDrag,
	disabled,
	selection,
	onSelectSource,
	onSelectAnswer,
	horizontalActions,
	verticalActions,
	pairCredits,
	pointsPerPair,
	scoreChip,
}: {
	availableParagraphs: Choice[];
	placedParagraphs: Choice[];
	activeDrag: DragData | null;
	disabled?: boolean;
	selection: Selection | null;
	onSelectSource: (id: string) => void;
	onSelectAnswer: (id: string) => void;
	horizontalActions: ArrowAction[];
	verticalActions: ArrowAction[];
	pairCredits?: (boolean | undefined)[];
	pointsPerPair?: number;
	scoreChip?: React.ReactNode;
}) => (
	<Box
		flexDirection="row"
		gap={8}
		alignItems="flex-start"
		justifyContent="center"
	>
		<SourceZone
			availableParagraphs={availableParagraphs}
			activeDrag={activeDrag}
			disabled={disabled}
			selectedId={selection?.zone === "source" ? selection.id : undefined}
			onSelect={onSelectSource}
		/>
		<ArrowButtonColumn direction="horizontal" actions={horizontalActions} />
		<AnswerZone
			placedParagraphs={placedParagraphs}
			activeDrag={activeDrag}
			disabled={disabled}
			selectedId={selection?.zone === "answer" ? selection.id : undefined}
			onSelect={onSelectAnswer}
			pairCredits={pairCredits}
			pointsPerPair={pointsPerPair}
			scoreChip={scoreChip}
		/>
		<ArrowButtonColumn direction="vertical" actions={verticalActions} />
	</Box>
);

export default ReorderParagraph;
